import React, { useEffect, useState } from 'react';
import { Archive, ShieldAlert } from 'lucide-react';
import api from '@/lib/api';
import { TurmasTab } from './TurmasTab';
import { CoberturaTab, LotesTab, MatrizesTab, PendenciasTab, SistemasTab } from './OutrasTabs';
import { BASE } from './ui';
import { NotasTab } from './NotasTab';
import { ModulosTab } from './ModulosTab';
import { FichaTab } from './FichaTab';
import { ImportadorTab } from './ImportadorTab';
import { IntegracaoTab } from './IntegracaoTab';
import { CargaBackupTab } from './CargaBackupTab';
import { ConsultaTab } from './ConsultaTab';

const TABS = [['turmas', 'Turmas 2010–2025'], ['matrizes', 'Matrizes'], ['sistemas', 'Sistemas de avaliação'], ['notas', 'Notas históricas'], ['ficha', 'Ficha do aluno (PDF)'],
  ['modulos', 'Financeiro, responsáveis e demais'], ['pendencias', 'Pendências'], ['cobertura', 'Cobertura 355'], ['lotes', 'Lotes e auditoria']];
const VIEWS = { carga: CargaBackupTab, consulta: ConsultaTab, turmas: TurmasTab, matrizes: MatrizesTab, sistemas: SistemasTab, notas: NotasTab, ficha: FichaTab, modulos: ModulosTab, importador: ImportadorTab, integracao: IntegracaoTab, pendencias: PendenciasTab, cobertura: CoberturaTab, lotes: LotesTab };

function Resumo({ res }) {
  return (
    <div className="overflow-x-auto rounded-xl border bg-white p-3" data-testid="acervo-resumo">
      <table className="w-full min-w-[640px] text-xs">
        <thead><tr className="text-left text-slate-500"><th className="p-1">Regime</th>{Object.keys(res.por_ano).map((y) => <th key={y} className="p-1">{y}</th>)}<th className="p-1">Total</th></tr></thead>
        <tbody>{['BIMESTRAL', 'TRIMESTRAL', 'SEM_MATRIZ_COMPROVADA'].map((r) => (
          <tr key={r} className="border-t"><td className="p-1 font-semibold">{r.replaceAll('_', ' ')}</td>{Object.values(res.por_ano).map((c, i) => <td key={i} className="p-1">{c[r] || '·'}</td>)}<td className="p-1 font-bold">{res.totais[r] || 0}</td></tr>))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-600"><b data-testid="acervo-resumo-turmas">{res.turmas}</b> turmas · pendências <b data-testid="acervo-total-pendencias">{res.total_pendencias}</b> ({Object.entries(res.pendencias_por_tipo).map(([k, v]) => `${k}: ${v}`).join(' · ')})</p>
    </div>
  );
}

const Vazio = ({ indisponivel }) => (
  <div className="rounded-2xl border bg-white p-8 text-center" data-testid="acervo-vazio">
    <Archive className="mx-auto mb-3 h-10 w-10 text-slate-300" />
    <p className="text-lg font-semibold text-slate-800">{indisponivel ? 'Banco histórico ainda não disponível neste ambiente' : 'Acervo histórico ainda não carregado neste ambiente'}</p>
    <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">{indisponivel ? 'A credencial atual não tem acesso ao banco histórico isolado. A consulta será liberada quando houver credencial dedicada e a carga for validada. ' : 'A consulta será liberada após a carga do backup original, a conciliação e a homologação (aba Carga do backup original, somente administradores). '}Importação, edição, exclusão e sincronização estão bloqueadas. Para 2026, valem exclusivamente os dados do DNACore.</p>
  </div>
);

export default function AcervoSponte() {
  const [st, setSt] = useState(null);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('turmas');
  useEffect(() => {
    api.get(`${BASE}/status`).then(({ data }) => {
      setSt(data);
      if (data.academico) api.get(`${BASE}/academico/resumo`).then((r) => setRes(r.data)).catch(() => {});
    }).catch((e) => setErr(e?.response?.status === 403 ? (e.response.data?.detail || 'Acesso restrito a Direção, Secretaria e Administrador.') : 'Módulo desligado neste ambiente.'));
  }, []);
  if (err) return <div className="p-8 text-sm text-slate-600" data-testid="acervo-bloqueado"><ShieldAlert className="mb-2 h-6 w-6" />{err}</div>;
  if (!st) return <div className="p-8 text-sm text-slate-500" data-testid="acervo-carregando">Carregando acervo…</div>;
  const prod = st.modo === 'producao_leitura';
  const admin = st.pode_importar;
  const vazio = st.indisponivel || (!st.academico && !(st.liberado && admin));
  const tabs = [...(st.liberado && admin ? [['consulta', 'Consulta do acervo'], ['modulos', 'Financeiro, responsáveis e demais']] : []),
    ...(st.academico && admin ? [['integracao', 'Integração DNACore']] : []), ...(st.academico ? TABS.filter(([k]) => !(k === 'modulos' && st.liberado && admin)) : []),
    ...(admin ? [['carga', 'Carga do backup original'], ['importador', 'Importador (admin)']] : [])];
  const atual = tabs.some(([k]) => k === tab) ? tab : tabs[0]?.[0];
  const View = atual && VIEWS[atual];
  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 md:p-8" data-testid="acervo-sponte-page">
      <div className="rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Archive className="h-7 w-7 text-amber-700" />
          <div className="min-w-0 sm:flex-1">
            <h1 className="text-2xl font-bold text-amber-950">Acervo Histórico / Sponte</h1>
            <p className="break-all text-sm text-amber-900">Origem histórica · consulta somente leitura · espaço <b>{st?.base_isolada || '…'}</b> ({st?.isolamento === 'BANCO_SEPARADO' ? 'banco separado' : `coleções ${st?.prefixo}*`}) · ambiente <b>{st?.ambiente}</b></p>
          </div>
          <span className="self-start rounded-full bg-amber-900 px-3 py-1 text-xs font-bold text-amber-50" data-testid="acervo-selo">{prod ? 'PRODUÇÃO · SOMENTE LEITURA · ADMINISTRADORES' : st?.dados_reais ? (st?.ambiente === 'clone_isolado_aprovado' ? 'CLONE ISOLADO · DADOS REAIS' : 'PREVIEW AUTORIZADO · DADOS REAIS · ACESSO RESTRITO') : 'HOMOLOGAÇÃO · DADOS FICTÍCIOS'}</span>
        </div>
        <p className="mt-2 text-xs text-amber-900" data-testid="acervo-aviso">{st?.aviso} Corte obrigatório: <b>somente 2010–2025</b>; 2026 usa exclusivamente as estruturas atuais do DNACore (Sponte 2026 = evidência isolada). Notas históricas: somente consulta, valores exatos da origem.</p>
      </div>
      {vazio && <Vazio indisponivel={st.indisponivel} />}
      {st.academico && res?.turmas > 0 && <Resumo res={res} />}
      {tabs.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="acervo-tabs">
          {tabs.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} data-testid={`acervo-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${atual === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{l}</button>)}
        </div>)}
      {View && <View />}
    </div>
  );
}
